// =============================================================================
// ui/tabs/GetStarted.tsx — the two cold-start banners on the Dashboard (#46).
// -----------------------------------------------------------------------------
// ── 🚨 THE FIRST SCREEN ANYONE SEES HAD NO NEXT ACTION ON IT ──
//
// A brand-new signup landed on a column of em-dashes and empty dials with
// nothing anywhere on the page telling them what to do. Every other item on the
// parity list improves the app for somebody already using it; this one decides
// whether they become a user at all.
//
// Two states, mutually exclusive, both of which clear themselves:
//
//   NO ACCOUNTS   trades attach to an account, so that is the gating first step
//   NO TRADES     they got past account setup and stopped
//
// ── 🚨 "HAVE YOU EVER TRADED" IS A WHOLE-BOOK QUESTION, NOT A RANGE ONE ──
//
// Beta gates the second banner on `state.trades`, which is everything it has
// loaded — not the active range. The distinction is the whole correctness of the
// banner: an established trader having a quiet week has zero trades IN RANGE,
// and telling them "you haven't logged a trade yet" is both false and insulting.
//
// So it reads the FACET UNIVERSE, which is all-time by construction and is
// already fetched by the toolbar's filter bar on every tab — no extra request.
//
// ⚠ NULL IS NOT EMPTY. The universe is null while loading and after a failed
// read, and rendering the banner then would flash "you haven't logged a trade
// yet" at an established user on every page load. Only a LOADED, EMPTY array
// counts as "never traded" — the same rule the preferences provider holds for
// the settings form, for the same reason.
//
// ── ✅ THE DEMO LINK IS BACK, BECAUSE THE FEATURE IS ──
//
// This paragraph used to say the link was left out because "this client has NO
// demo mode — not on this screen, not in the shell, nowhere", and shipping a
// button that does nothing is worse than the gap. That was right at the time and
// is now stale: the demo exists (#90), so beta's third line is restored.
//
// 🚨 IT IS ALSO A PROMISE ON THE LIVE PRICING PAGE. "Pattern tagging + a full
// Demo account" is a FREE-tier bullet on the page selling the product, and this
// screen — the one a brand-new signup lands on with no accounts and no trades —
// is where somebody would go looking for it.
//
// ⚠ The paragraph this replaced pointed at #46 as the tracking issue. #46 is a
// DIFFERENT, closed issue (the empty-dashboard cold start); the demo has always
// been #63. A stale pointer sends the next reader to a resolved thread.
//
// ⚠ Beta ALSO offers the demo from its account picker, so an established user
// can load it any time. That entry point is not ported; this one covers the
// path the promise is made on.
// =============================================================================

import type { TradesIntent } from '../shell/TradeActions';
import type { AccountsIntent } from './Accounts';

export interface GetStartedProps {
  /** Loaded accounts, or null while the read is in flight. */
  accounts: readonly unknown[] | null;
  /**
   * All-time trade rows, or null while loading / after a failed read.
   *
   * ⚠ See the header: null must not be treated as empty.
   */
  universe: readonly unknown[] | null;
  /** Send the reader somewhere. `intent` pre-arms the Trades tab. */
  go: (tab: string, intent?: TradesIntent | AccountsIntent) => void;
  /**
   * Load the sample book.
   *
   * ⚠ OPTIONAL, and its absence hides the link rather than rendering a dead one.
   * Every existing test mounts this component without it, and a button that does
   * nothing is the exact failure the paragraph above talks itself out of.
   */
  onLoadDemo?: (() => void) | undefined;
}

export function GetStarted({ accounts, universe, go, onLoadDemo }: GetStartedProps) {
  // Nothing to say until we actually know. Both reads must have landed.
  if (accounts === null || universe === null) return null;

  if (accounts.length === 0) {
    return (
      <div className="get-started" data-testid="get-started" data-kind="no-accounts">
        <div className="get-started-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width={18} height={18} fill="none"
            stroke="var(--blue)" strokeWidth={2.2}>
            <path d="M12 5v14" /><path d="M5 12h14" />
          </svg>
        </div>
        <div className="get-started-body">
          <div className="get-started-title">Create your first account to get started.</div>
          <div className="get-started-sub">
            Your trades attach to a trading account. Set one up — about 30 seconds —
            then import or log trades.
          </div>
        </div>
        {/* ⚠ SECONDARY, and to the LEFT of the primary action. Beta puts the
            demo below its main call to action for the same reason: the reader
            who came to journal their own trades must not be nudged into a
            sample book instead. */}
        {onLoadDemo ? (
          <button type="button" className="btn" onClick={onLoadDemo}>
            👀 Just exploring? Load the Demo Account
          </button>
        ) : null}
        {/* ⚠ `'new'` ARMS THE ACCOUNTS TAB, so this lands in the form rather than
            on a screen where the reader has to find "New Account" and click a
            second time — which is the one thing a first-run user should not have
            to do. Beta opens its modal IN PLACE (index.html:27945); this client
            navigates and opens there instead, the same trade-off already made and
            written down for Add Trade and Import in `shell/TradeActions.tsx`. */}
        <button type="button" className="btn primary" onClick={() => go('accounts', 'new')}>
          + Create your first account
        </button>
      </div>
    );
  }

  // ⚠ Beta's exact condition: accounts exist AND the book is empty. The two
  // banners are mutually exclusive, which is why this is an `else` rather than a
  // second independent check — showing both at once would offer a first trade to
  // someone with nowhere to put it.
  if (universe.length === 0) {
    return (
      <div className="get-started" data-testid="get-started" data-kind="no-trades">
        <div className="get-started-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width={18} height={18} fill="none"
            stroke="var(--blue)" strokeWidth={2.2}>
            <path d="M3 3v18h18" /><path d="M7 14l4-4 4 4 5-5" />
          </svg>
        </div>
        <div className="get-started-body">
          <div className="get-started-title">You haven’t logged a trade yet.</div>
          <div className="get-started-sub">
            Import a CSV of your history, or log a trade manually.
          </div>
        </div>
        {/* ⚠ Both reuse the intent the toolbar already uses — the Trades tab is
            armed BEFORE the navigate, because it remounts on arrival and has to
            know what it was asked to do by the time it mounts. */}
        <button type="button" className="btn" onClick={() => go('trades', 'import')}>
          Import CSV
        </button>
        <button type="button" className="btn primary" onClick={() => go('trades', 'add')}>
          + Log first trade
        </button>
      </div>
    );
  }

  return null;
}
